<template>
  <div v-click-outside="hideOptions" class="select">
    <button
      :class="{
        'select--button__capitalize': capitalize
      }"
      :disabled="disabled"
      ref="select-trigger"
      type="button"
      class="select--button"
      @click="showOptions"
      @focus="$emit('focus', $event)"
    >
      <span
        v-if="
          (Array.isArray(selectedValues) && selectedValues.length === 0) ||
            !selectedValues
        "
        class="label label__placeholder"
      >
        {{ placeholder }}
      </span>

      <span v-else :title="selectedsLabel" class="label label__selected">
        <slot name="button-label" :selectedValues="selectedValues"
          >{{ capitalize ? selectedsLabel.toLowerCase() : selectedsLabel }}
        </slot>
      </span>
      <i
        class="select--button__caret"
        :class="{
          rotate: show
        }"
      ></i>
    </button>

    <!-- OPTIONS -->
    <div v-show="show" ref="select-options" class="select-options">
      <!-- SLOT DEFAULT -->
      <slot>
        <SearchInput
          v-if="search"
          v-model="searchTermComp"
          :placeholder="searchPlaceholder"
          class="select-search"
        />
        <div class="select-options-list">
          <!-- INPUT PARA SELEÇÃO DE TODAS OPÇÕES -->
          <div v-if="toggleAll && multiple" class="select-options--toggle">
            <InputCheckbox
              :label="toggleAllLabel"
              :checked="allChecked"
              :disabled="
                maxQuantitySelection
                  ? value.length >= maxQuantitySelection
                  : false
              "
              @change="toggleAllEvent"
            />
          </div>

          <!-- OPÇÃO -->
          <SelectCouponOption
            v-model="selectedValues"
            v-for="(option, i) in searchResult"
            :key="`select-option-${option?.codigo || i}`"
            :option="option"
            :multiple="multiple"
            :capitalize="capitalize"
            :disableSelectOption="disableSelectOption"
            @input="$emit('input', selectedValues)"
            @change="$emit('change', selectedValues)"
          />
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside'
import InputCheckbox from '../InputCheckbox'
import SearchInput from '../SearchInput'
import SelectCouponOption from './SelectCouponOption'
import popperMixin from '@/mixins/popperMixin'
import vScroll from '@/directives/vScroll'
import debounce from 'lodash.debounce'
import { removeStringAccents } from '@/helpers/string'

/**
 * Componente do elemento select usado para as tarifas de cupons.
 * Esse componente foi modificado especificamente para esse caso em cupons,
 * caso queira reutilizar o mesmo, é necessário verificar se ele atende a regra de negócio
 * da parte em que for usado.
 */
export default {
  name: 'SelectCoupon',
  mixins: [popperMixin],

  directives: {
    clickOutside: vClickOutside.directive,
    scroll: vScroll
  },

  components: {
    SearchInput,
    SelectCouponOption,
    InputCheckbox
  },
  props: {
    /** Valor do select */
    value: {
      type: [Array, Object, Number, String]
    },
    /** Placeholder do select */
    placeholder: {
      type: String,
      default: 'Selecione'
    },
    /** Se possuí campo de busca */
    search: {
      type: Boolean,
      default: false
    },
    /** Placeholder do campo de busca */
    searchPlaceholder: {
      type: String,
      default: 'Buscar'
    },
    /** Se é permitido selecionar multiplas opções */
    multiple: {
      type: Boolean,
      default: false
    },
    /** Opções do select */
    options: {
      type: Array,
      default: () => []
    },
    /** Se deve apresentar uma opção para selecionar todas as opções */
    toggleAll: {
      type: Boolean,
      default: true
    },
    /** Texto da opção de seleção de todas */
    toggleAllLabel: {
      type: String,
      default: 'Todas'
    },
    /** Se deve apresentar o texto de todas opções selecionadas */
    showAllOptionsSelectedText: {
      type: Boolean,
      default: true
    },
    /** Se deve capitalizar os textos das opções e opção selecionada */
    capitalize: {
      type: Boolean,
      default: false
    },
    /** Desabilita a seleção */
    disabled: {
      type: Boolean,
      default: false
    },
    /** Se deve carregar as opções sob demanda */
    loadOnDemand: {
      type: Boolean,
      default: false
    },
    /** Se o cupom já foi utilizado */
    wasUsed: {
      type: Boolean,
      default: false
    },
    /** Quantidade de opções visíveis inicialmente quando usado o loadOnDemand */
    initialOptionsVisible: {
      type: Number,
      default: 30
    },

    /** Quantidade máxima que pode ser selecionada, quando multi */
    maxQuantitySelection: Number,

    hotelLabelKey: {
      type: String,
      default: 'nome'
    },
    rateLabelKey: {
      type: String,
      default: 'descricao'
    },
    rateValueKey: {
      type: String,
      default: 'codigo'
    }
  },
  data() {
    return {
      selectedValues: null,
      searchTerm: '',
      show: false /**  flag que controla a visibilidade das opções*/,
      /**
       * Parâmetros usados para controle de listas muito grandes
       * de opções
       */
      currentOptionsPage: 0 /** Página a ser adicionada */,
      currentOptions: [] /** Opções listadas atualmente */,
      scrollElement: null /** Elemento com scroll de opções */
    }
  },
  computed: {
    /**
     * Retorna o texto com o nome de todos os itens selecionados
     * @returns {String}
     * */
    selectedsLabel() {
      if (this.showAllOptionsSelectedText && this.allChecked) {
        return this.toggleAllLabel
      }

      return Array.isArray(this.selectedValues)
        ? this.selectedValues.map((item) => item[this.rateLabelKey]).join(', ')
        : this.selectedValues[this.rateLabelKey]
    },
    /**
     * Propriedade que filtra as opções de acordo com o termo buscado
     * @returns {Array}
     */
    searchResult() {
      const term = removeStringAccents(this.searchTerm)

      if (term.trim() === '') {
        this.$emit('search', [])
        return this.currentOptions
      }

      const rgx = new RegExp(term, 'gmi')

      const result = []

      this.options.forEach((item) => {
        const rateName = removeStringAccents(item[this.rateLabelKey])
        const hotelName = removeStringAccents(item[this.hotelLabelKey])

        /** Filtrar pelo nome da tarifa */
        if (rateName?.search(rgx) > -1) {
          result.push(item)
        }

        /** Se é um grupo filtra os filhos */
        if (item[this.hotelLabelKey]) {
          /** Filtrar pelo nome do hotel, se match adiciona todas as tarifas no resultado */
          if (hotelName?.search(rgx) > -1) {
            result.push(item)

            return
          }

          const itemCopy = { ...item }
          const group = [...item.options]
          itemCopy.options = group.filter(
            (item) =>
              removeStringAccents(item[this.rateLabelKey]).search(rgx) > -1
          )

          if (itemCopy.options.length > 0) result.push(itemCopy)
        }
      })

      this.$emit('search', result)

      return result
    },

    searchResultRates() {
      return this.searchResult.flatMap((item) => item.options || item)
    },

    searchTermComp: {
      get: function() {
        return this.searchTerm
      },

      set: debounce(function(newValue) {
        this.searchTerm = newValue
      }, 500)
    },

    /**
     * Retorna se todas as opções listadas estão selecionadas
     * @returns {Boolean}
     */
    allChecked() {
      const allOptions = (this.searchTerm.trim() !== ''
        ? this.searchResult
        : this.options
      )
        .flatMap((item) => item.options || item)
        .filter((item) => !item.disabled)

      return allOptions.every(
        (item) =>
          this.selectedValues.findIndex(
            (selItem) => +selItem.codigo === +item.codigo
          ) > -1
      )
    },

    activeOptions() {
      return this.options
        .flatMap((item) => item.tarifas || item.options || item)
        .filter((item) => item.ativo !== false)
    }
  },
  watch: {
    value(newVal) {
      if (this.multiple) {
        this.selectedValues = newVal || []
      } else {
        this.selectedValues = newVal
      }
    },

    options(newVal) {
      this.currentOptions = newVal
    },

    searchTermComp() {
      this.$refs['select-options'].scrollTo(0, 0)
    }
  },

  created() {
    this.selectedValues = this.value

    if (this.multiple) {
      this.selectedValues = this.value || []
      this.currentOptions = this.options
    } else {
      this.currentOptions = this.options
    }
  },

  mounted() {
    if (this.multiple) {
      /** verifica se todas as opções estão selecionadas */
      this.selectAll = this.allChecked
    }
  },

  methods: {
    /**
     * Desabilita uma opção da listagem
     * @param {object} option opção de tarifa
     */
    disableSelectOption(option) {
      if (
        this.maxQuantitySelection &&
        this.value.findIndex((item) => item.codigo === option.codigo) < 0
      ) {
        return this.value.length >= this.maxQuantitySelection
      }

      return false
    },

    /** Esconde a lista de opções */
    hideOptions() {
      this.show = false

      if (this.scrollElement) {
        this.scrollElement.scrollTop = 0
      }

      setTimeout(() => {
        this.searchTerm = ''
      }, 500)

      /** Emite um evento de blur */
      this.$emit('blur')
    },

    /** Gerenciador do evento para input de multipla seleção sem grupos */
    eventHandler() {
      this.$emit('input', this.selectedValues)
      this.$emit('change', this.selectedValues)

      if (!this.multiple) {
        this.hideOptions()
      }
    },

    /** Apresenta a lista de opções */
    showOptions() {
      this.show = !this.show

      if (this.show) {
        this.createPopper(
          this.$refs['select-trigger'],
          this.$refs['select-options'],
          this.position
        )
      } else {
        this.destroyPopper()
      }

      /** Emite um evento de focus */
      this.$emit('focus')
    },

    /** Marca ou desmarca todas as opções apresentadas */
    toggleAllEvent(checked) {
      /** Pegar itens desabilitados */
      const disableds = this.selectedValues.filter((el) => el.disabled)

      const arrayToIterate =
        this.searchTerm.trim() === ''
          ? this.activeOptions
          : this.searchResultRates

      if (checked) {
        this.selectedValues = Array.from(
          new Set([...this.selectedValues, ...arrayToIterate, ...disableds])
        )
      } else {
        /** Se for multipla seleção */
        if (this.multiple) {
          this.selectedValues = this.selectedValues.filter(
            (item) =>
              arrayToIterate.findIndex((opt) => opt.codigo === item.codigo) < 0
          )
        } else {
          this.selectedValues = null
        }
      }

      this.$emit('input', this.selectedValues)
      this.$emit('change', this.selectedValues)
    }
  }
}
</script>

<style lang="scss" scoped>
.select {
  position: relative;

  &-options {
    display: flex;
    flex-wrap: wrap;

    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    z-index: $dropdown-options-z-index;

    min-width: 100%;
    max-height: 285px;
    overflow-y: auto;
    padding: 15px 0;
    border: 1px solid $gray-border;
    background-color: $white;

    &--toggle {
      padding-bottom: 12px;
      margin-bottom: 14px;
      border-bottom: 1px solid $gray-border;
    }

    &-list {
      width: 100%;
      padding: 0 20px;
    }
  }

  &-search {
    width: calc(100% - 22px * 2);
    margin: 0 auto 18px;
    background-color: #fff;
  }

  &--button {
    position: relative;
    width: 100%;
    height: $input-height;
    padding: 0;
    appearance: none;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    border: none;
    background: none;
    text-align: left;
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-normal');
    color: $gray-darkest;
    border-bottom: 1px solid $gray-darkest;

    &[disabled] {
      opacity: 0.5;
    }

    .label {
      width: calc(100% - 15px);
      max-width: 450px;
      line-height: 21px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &__placeholder {
        color: $gray-dark;
      }

      &__selected {
        color: $gray-darkest;
      }
    }

    &__caret {
      display: inline-block;
      transition: all 0.25s 0.25s;
      transform: rotate(0);

      &.rotate {
        transform: rotate(180deg);
      }

      &:after {
        content: '';
        display: inline-block;
        @include arrow(bottom, $gray-darkest, 5px);
      }
    }

    &__capitalize {
      text-transform: capitalize;
    }
  }

  ::v-deep .select-option {
    button,
    label {
      text-transform: capitalize;
    }
  }
}
</style>
